// src/pages/Exams/generate/QuickModeForm.tsx
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Blueprint, DifficultyMode } from './types';
import { useSchoolGrades, useSchoolGradeSubjects } from '../../../react-query/queries';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { useSchoolChapters } from './hooks/useSchoolChapters';
import { useBookList } from './hooks/useBookList';
import { gradeLabel } from '../../../school_panel/lib/gradeLabel';
import { usePrediction } from './hooks/usePrediction';
import { predictionPatch } from './prediction';
import { Link } from 'react-router-dom';
import { MoreOptions, GEN_INPUT } from './MoreOptions';
import { NAME_MAX } from '../../../school_panel/lib/fieldClass';
import { Button, FormField, InlineSelect, PanelCard, PillTabs, Segmented, Term } from '../../../school_panel/components/ui';
import OverflowRow from '../../../school_panel/components/ui/OverflowRow';
import { Check, ChevronDown, Minus, Plus, Sparkles } from 'lucide-react';

interface Props {
  blueprint: Blueprint;
  patch: (patch: Partial<Blueprint>) => void;
  onStart: () => void;
  startError: string | null;
  onSaveTemplate?: (name: string) => void;
  /** Saved templates for the "Template" picker; the picker is hidden when omitted. */
  templates?: Array<{ id: number; name: string; blueprint: Blueprint }>;
  onLoadTemplate?: (blueprint: Blueprint) => void;
}

// Design `.lbl`: 13px semibold ink.
const LBL = 'mb-1.5 block text-[13px] font-semibold text-ink';

const FLOW_STEPS: Array<{ title: string; note: string }> = [
  { title: 'Fetch source material', note: 'From the linked textbook chapters' },
  { title: 'Generate questions', note: 'Four choices, answer and explanation each' },
  { title: 'Score quality', note: 'Weak or ambiguous items are flagged' },
  { title: 'You review and save', note: 'Nothing reaches students until you save' },
];

// The generator reads only `difficulty_pct`, so every preset is just a split.
const DIFF_PRESETS: Array<{ value: DifficultyMode; label: string;
  pct: { easy: number; medium: number; hard: number } }> = [
  { value: 'mixed',  label: 'Mixed',  pct: { easy: 40, medium: 40, hard: 20 } },
  { value: 'easy',   label: 'Easy',   pct: { easy: 80, medium: 20, hard: 0 } },
  { value: 'medium', label: 'Medium', pct: { easy: 20, medium: 60, hard: 20 } },
  { value: 'hard',   label: 'Hard',   pct: { easy: 10, medium: 40, hard: 50 } },
];

export function QuickModeForm({ blueprint, patch, onStart, startError, onSaveTemplate, templates, onLoadTemplate }: Props) {
  const { token } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const { data: gradesData } = useSchoolGrades(token);
  const { data: subjectsData } = useSchoolGradeSubjects(
    token, blueprint.grade_id ? String(blueprint.grade_id) : '0');
  const { data: chapters } = useSchoolChapters(blueprint.subject_id);
  const { data: books } = useBookList(blueprint.subject_id);
  // A chosen national exam fills the chapters from its predicted next paper.
  const { data: prediction, isLoading: predictionLoading } = usePrediction(
    blueprint.national_exam_id, blueprint.subject_id, blueprint.total_questions);

  // Chapters sit by grade: the exam's grade first, the others only when the teacher opens them.
  const gradeId = blueprint.grade_id;
  const gradeNameOf = (id: number) => {
    const g = (gradesData?.grades ?? []).find((x: any) => x.id === id);
    return g ? (g.grade_name || g.name) : null;
  };
  // A chapter with no grade tag can't be placed, so it sits with the exam's grade.
  const chapterGradeOf = (c: { school_grade_ids?: number[] }) => {
    const ids = c.school_grade_ids ?? [];
    if (gradeId != null && (ids.length === 0 || ids.includes(gradeId))) return gradeId;
    return ids[0] ?? 0;
  };
  const gradeChapters = useMemo(
    () => (chapters ?? []).filter(c => gradeId != null && chapterGradeOf(c) === gradeId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chapters, gradeId]);
  const bookIdsForGrades = (ids: number[]) =>
    (books ?? []).filter(b => (b.school_grade_ids ?? []).some(g => ids.includes(g))).map(b => b.id);

  // Book sources start as the exam grade's books (once per grade + subject); the teacher adds more.
  const booksInitFor = useRef('');
  useEffect(() => {
    if (!books || gradeId == null || !blueprint.subject_id) return;
    const key = `${blueprint.subject_id}:${gradeId}`;
    if (booksInitFor.current === key) return;
    booksInitFor.current = key;
    if (blueprint.book_ids_included.length === 0) {
      const own = bookIdsForGrades([gradeId]);
      if (own.length > 0) patch({ book_ids_included: own });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [books, gradeId, blueprint.subject_id]);

  // Auto-fill chapter_distribution when chapters or NE changes
  useEffect(() => {
    if (!chapters || chapters.length === 0) return;
    if (blueprint.chapter_distribution.length > 0) return;
    // With a national exam selected, wait for its (async) prediction before filling;
    // otherwise the even split below runs first and the length>0 guard keeps it.
    if (blueprint.national_exam_id && predictionLoading) return;
    if (blueprint.national_exam_id && prediction?.available && prediction.chapters.some(c => c.predicted > 0)) {
      patch(predictionPatch(prediction));
      return;
    }
    // Only the exam grade's chapters to start with (all of them when none are tagged with it).
    const pool = gradeChapters.length > 0 ? gradeChapters : chapters;
    const per = Math.max(1, Math.floor(blueprint.total_questions / pool.length));
    patch({ chapter_distribution: pool.map(c => ({ chapter_id: c.id, count: per, difficulty_override: null })) });
  }, [chapters, gradeChapters, prediction, predictionLoading, blueprint.national_exam_id,
      blueprint.chapter_distribution.length,
      blueprint.total_questions, patch]);

  const onDiffPreset = (mode: DifficultyMode) => {
    const preset = DIFF_PRESETS.find(p => p.value === mode)!;
    patch({ difficulty_mode: mode, difficulty_pct: preset.pct });
  };

  // Chapter chips (design): a multi-select over this subject's chapters. Every
  // pick re-spreads the question total evenly over the chosen chapters, keeping
  // any per-chapter difficulty already set under More options.
  const chapterList = chapters ?? [];
  const countOf = (id: number) => blueprint.chapter_distribution.find(e => e.chapter_id === id)?.count ?? 0;
  const selectedIds = chapterList.filter(c => countOf(c.id) > 0).map(c => c.id);
  const spreadOver = (ids: number[], total = blueprint.total_questions, extraPatch: Partial<Blueprint> = {}) => {
    const k = ids.length;
    // When there are fewer questions than chapters, only the first `total` get one each.
    const base = k === 0 ? 0 : Math.floor(total / k);
    const extra = k > 0 ? total % k : 0;
    patch({
      ...extraPatch,
      chapter_distribution: chapterList.map(c => {
        const at = ids.indexOf(c.id);
        return {
          chapter_id: c.id,
          count: at < 0 ? 0 : base + (at < extra ? 1 : 0),
          difficulty_override: blueprint.chapter_distribution.find(e => e.chapter_id === c.id)?.difficulty_override ?? null,
        };
      }),
    });
  };
  const toggleChapter = (id: number) => {
    const turningOn = !selectedIds.includes(id);
    const chapter = chapterList.find(c => c.id === id);
    // A chapter from another grade brings that grade's book along, or it has no source material.
    const extraBooks = turningOn && chapter ? bookIdsForGrades(chapter.school_grade_ids ?? [])
      .filter(b => !blueprint.book_ids_included.includes(b)) : [];
    spreadOver(
      chapterList.map(c => c.id).filter(cid => (cid === id ? turningOn : selectedIds.includes(cid))),
      blueprint.total_questions,
      extraBooks.length && blueprint.book_ids_included.length ? { book_ids_included: [...blueprint.book_ids_included, ...extraBooks] } : {});
  };

  // Groups: [gradeId, chapters] with the exam grade first, then the school's grade order.
  const [openGrades, setOpenGrades] = useState<number[]>([]);
  const groups = useMemo(() => {
    const m = new Map<number, typeof chapterList>();
    for (const c of chapterList) {
      const g = chapterGradeOf(c);
      m.set(g, [...(m.get(g) ?? []), c]);
    }
    const order = [gradeId ?? -1, ...(gradesData?.grades ?? []).map((g: any) => g.id as number), 0];
    return Array.from(m.entries()).sort((a, b) => {
      const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0]);
      return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterList, gradeId, gradesData]);
  const isShown = (g: number) => g === gradeId || openGrades.includes(g)
    || (groups.find(([k]) => k === g)?.[1] ?? []).some(c => selectedIds.includes(c.id));
  const shownIds = groups.filter(([g]) => isShown(g)).flatMap(([, cs]) => cs.map(c => c.id));
  const groupLabel = (g: number) => (g === 0 ? 'No grade' : gradeLabel(gradeNameOf(g) ?? String(g)));

  // A new total keeps the chapters the teacher picked and re-spreads over them;
  // with nothing picked yet it clears so the auto-fill can choose.
  const setTotal = (n: number) => {
    const total = Math.max(1, Math.min(200, n));
    if (selectedIds.length > 0) spreadOver(selectedIds, total, { total_questions: total });
    else patch({ total_questions: total, chapter_distribution: [] });
  };

  const canSubmit =
    !!blueprint.grade_id &&
    !!blueprint.subject_id &&
    blueprint.name.trim().length >= 3 &&
    blueprint.total_questions >= 1 &&
    blueprint.chapter_distribution.some(e => e.count > 0);

  // Why the Generate button is off, in the order the user would fix things.
  const noChapters = !!blueprint.subject_id && chapters !== undefined && chapters.length === 0;
  let blockedReason: ReactNode = null;
  if (!blueprint.grade_id) blockedReason = 'Choose a grade to continue.';
  else if (!blueprint.subject_id) blockedReason = 'Choose a subject to continue.';
  else if (noChapters) {
    blockedReason = (
      <>
        No chapters for this grade and subject yet — add them in{' '}
        <Link to="/school-setup/chapters" className="font-semibold text-on-navy underline">Chapter Setup</Link>.
      </>
    );
  }
  else if (blueprint.name.trim().length < 3) blockedReason = 'Enter an exam name of at least 3 characters.';
  else if (blueprint.total_questions < 1) blockedReason = 'Enter at least 1 question.';
  else if (chapterList.length > 0 && !blueprint.chapter_distribution.some(e => e.count > 0)) blockedReason = 'Pick at least one chapter.';
  else if (!canSubmit) blockedReason = 'Loading chapters…';

  // 5s per chapter per form, the estimate the old summary card showed.
  const chaptersWithCount = blueprint.chapter_distribution.filter(e => e.count > 0).length;
  const estSeconds = Math.max(5, 5 * chaptersWithCount * blueprint.num_forms);
  const estMinutes = Math.max(1, Math.round(estSeconds / 60));

  // The rail sentence reads straight off the blueprint plus the chosen names.
  const gradeName: string | undefined = gradesData?.grades
    ?.find((g: any) => g.id === blueprint.grade_id)?.grade_name
    ?? gradesData?.grades?.find((g: any) => g.id === blueprint.grade_id)?.name;
  const subjectName: string | undefined = subjectsData?.subjects
    ?.find((s: any) => s.id === blueprint.subject_id)?.subject_name
    ?? subjectsData?.subjects?.find((s: any) => s.id === blueprint.subject_id)?.name;
  const diffLabel = (DIFF_PRESETS.find(p => p.value === blueprint.difficulty_mode)?.label
    ?? blueprint.difficulty_mode ?? 'mixed').toLowerCase();
  const headline =
    `A ${blueprint.total_questions}-question ${diffLabel}${subjectName ? ` ${subjectName}` : ''} exam` +
    `${gradeName ? ` for ${gradeName}` : ''}`;
  const pct = blueprint.difficulty_pct ?? DIFF_PRESETS[0].pct;

  return (
    <div className="grid grid-cols-1 items-start gap-[18px] xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <PanelCard
        title="Set up"
        subtitle="Start from a template or fill in the basics."
        actions={templates ? (
          <InlineSelect label="Template" aria-label="Start from template" boxClassName="!min-h-[38px]" value={templateId}
            onChange={(e) => {
              const id = e.target.value;
              setTemplateId(id);
              const t = templates.find(x => String(x.id) === id);
              if (t) onLoadTemplate?.(t.blueprint);
            }}>
            <option value="">None</option>
            {templates.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </InlineSelect>
        ) : undefined}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="min-w-0 md:col-span-2">
            <span className={LBL}>Grade</span>
            {(gradesData?.grades ?? []).length > 0 ? (
              <PillTabs
                label="Grade"
                scroll dense
                options={(gradesData?.grades ?? []).map((g: any) => ({ key: String(g.id), label: g.grade_name || g.name }))}
                value={blueprint.grade_id != null ? String(blueprint.grade_id) : ''}
                onChange={(k) => { setOpenGrades([]); patch({ grade_id: Number(k), subject_id: null, chapter_distribution: [], book_ids_included: [] }); }}
              />
            ) : (
              <p role="group" aria-label="Grade" className="text-sm text-muted">No grades yet.</p>
            )}
          </div>

          <div className="min-w-0 md:col-span-2">
            <span className={LBL}>Subject</span>
            {!blueprint.grade_id ? (
              <p role="group" aria-label="Subject" className="text-sm text-muted">Choose a grade first.</p>
            ) : (subjectsData?.subjects ?? []).length > 0 ? (
              <PillTabs
                label="Subject"
                scroll dense
                options={(subjectsData?.subjects ?? []).map((s: any) => ({ key: String(s.id), label: s.subject_name || s.name }))}
                value={blueprint.subject_id != null ? String(blueprint.subject_id) : ''}
                onChange={(k) => { setOpenGrades([]); patch({ subject_id: Number(k), chapter_distribution: [], book_ids_included: [] }); }}
              />
            ) : (
              <p role="group" aria-label="Subject" className="text-sm text-muted">No subjects for this grade.</p>
            )}
          </div>

          <div className="md:col-span-2">
            <FormField label="Exam name" htmlFor="gen-name">
              <input
                id="gen-name"
                className={GEN_INPUT}
                value={blueprint.name}
                onChange={(e) => patch({ name: e.target.value })}
                maxLength={NAME_MAX}
                placeholder="e.g. Chemistry mid-term"
              />
            </FormField>
          </div>

          <div>
            <label htmlFor="gen-total" className={LBL}>Number of questions</label>
            <div className="flex items-center gap-3">
              <span role="group" aria-label="Question count stepper"
                className="inline-flex items-center overflow-hidden rounded-[10px] border border-line-strong bg-surface">
                <button type="button" aria-label="Five fewer questions"
                  onClick={() => setTotal(blueprint.total_questions - 5)}
                  className="grid h-[34px] w-[34px] place-items-center bg-panel text-ink hover:bg-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                  <Minus size={16} strokeWidth={1.8} aria-hidden />
                </button>
                <input
                  id="gen-total"
                  type="number" min={1} max={200}
                  aria-live="polite"
                  className="h-[34px] w-14 border-0 bg-transparent text-center font-mono text-sm font-semibold text-ink [appearance:textfield] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  value={blueprint.total_questions}
                  onChange={(e) => setTotal(Number(e.target.value) || 1)}
                />
                <button type="button" aria-label="Five more questions"
                  onClick={() => setTotal(blueprint.total_questions + 5)}
                  className="grid h-[34px] w-[34px] place-items-center bg-panel text-ink hover:bg-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                  <Plus size={16} strokeWidth={1.8} aria-hidden />
                </button>
              </span>
              <span className="text-xs text-muted">steps of 5 · max 200</span>
            </div>
          </div>

          <FormField label="Duration (minutes)" htmlFor="gen-duration">
            <input
              id="gen-duration"
              type="number" min={5} max={300}
              className={GEN_INPUT}
              value={blueprint.duration_min}
              onChange={(e) => patch({ duration_min: Math.max(5, Number(e.target.value) || 60) })}
            />
          </FormField>
        </div>

        <div className="mt-5">
          <span className={LBL}><Term k="gen_difficulty">Difficulty</Term></span>
          <div className="flex flex-wrap items-center gap-4">
            <Segmented<DifficultyMode>
              label="Difficulty"
              options={DIFF_PRESETS.map(p => ({ key: p.value, label: p.label }))}
              value={blueprint.difficulty_mode}
              onChange={onDiffPreset}
            />
            {blueprint.difficulty_mode === 'mixed' && (
              <div className="min-w-[220px] flex-[1_1_220px]" data-testid="difficulty-mix">
                <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-[5px]" aria-hidden>
                  <span className="bg-diff-easy" style={{ flex: pct.easy }} />
                  <span className="bg-slate" style={{ flex: pct.medium }} />
                  <span className="bg-navy" style={{ flex: pct.hard }} />
                </div>
                <p className="mt-1.5 flex gap-3.5 text-xs text-muted">
                  <span>Easy {pct.easy}%</span><span>Medium {pct.medium}%</span><span>Hard {pct.hard}%</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {chapterList.length > 0 && (
          <div className="mt-6">
            <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-3">
              <p id="gen-chapters-label" className="text-[13px] font-semibold text-ink">
                Chapters <span className="font-normal text-muted">· {selectedIds.length} of {shownIds.length} selected</span>
              </p>
              <span className="flex gap-1.5">
                <button type="button" onClick={() => spreadOver(shownIds)}
                  className="inline-flex min-h-[34px] items-center rounded-[10px] border border-line-strong bg-surface px-[11px] text-xs font-semibold text-ink hover:border-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                  Select all
                </button>
                <button type="button" onClick={() => spreadOver([])}
                  className="inline-flex min-h-[34px] items-center rounded-[10px] border border-line-strong bg-surface px-[11px] text-xs font-semibold text-ink hover:border-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                  Clear
                </button>
              </span>
            </div>
            <div className="space-y-3.5">
              {groups.filter(([g]) => isShown(g)).map(([g, cs]) => (
                <div key={g}>
                  <div className="mb-1.5 flex items-center gap-2">
                    <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{groupLabel(g)}</span>
                    {g !== gradeId && !cs.some(c => selectedIds.includes(c.id)) && (
                      <button type="button" onClick={() => setOpenGrades(o => o.filter(x => x !== g))}
                        className="text-xs font-semibold text-primary underline">Hide</button>
                    )}
                  </div>
                  <OverflowRow role="group" label={g === gradeId ? 'Chapters' : `Chapters · ${groupLabel(g)}`} rowClassName="gap-2">
                    {cs.map(c => {
                      const on = selectedIds.includes(c.id);
                      // Design `.chipx`: a 12px-radius chip with a square check box and the question share.
                      return (
                        <button key={c.id} type="button" aria-pressed={on} onClick={() => toggleChapter(c.id)}
                          className={`inline-flex min-h-[40px] shrink-0 items-center gap-2 whitespace-nowrap rounded-[12px] border-[1.5px] py-0 pl-2.5 pr-3.5 text-left text-[13px] font-medium text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                            on ? 'border-accent bg-accent-bg' : 'border-line-strong bg-surface hover:border-slate'}`}>
                          <span aria-hidden className={`grid h-[18px] w-[18px] flex-none place-items-center rounded-[6px] border-[1.5px] ${on ? 'border-accent bg-accent text-navy' : 'border-control'}`}>
                            {on && <Check size={12} strokeWidth={2.6} />}
                          </span>
                          <span>{c.name}</span>
                          {on && <span className="font-mono text-xs text-muted">{countOf(c.id)} Q</span>}
                        </button>
                      );
                    })}
                  </OverflowRow>
                </div>
              ))}
              {groups.some(([g]) => !isShown(g)) && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted">Add chapters from another grade:</span>
                  {groups.filter(([g]) => !isShown(g)).map(([g, cs]) => (
                    <button key={g} type="button" onClick={() => setOpenGrades(o => [...o, g])}
                      className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-dashed border-line-strong px-3 text-xs font-semibold text-ink hover:border-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                      <Plus size={12} aria-hidden />{groupLabel(g)} <span className="font-normal text-muted">({cs.length})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-1.5 text-xs text-muted">
              Questions are spread evenly across selected chapters. Fine-tune per chapter under More options. Missing a chapter? Add it in{' '}
              <Link to="/school-setup/chapters" className="text-ink underline">Chapter Setup</Link>.
            </p>
          </div>
        )}

        <div className="mt-[22px]">
          <button
            type="button"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(o => !o)}
            className="flex min-h-[52px] w-full items-center justify-between gap-3 border-0 border-t border-hairline bg-transparent px-1 text-left font-display text-[15px] font-bold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            <span>More options <span className="font-sans text-[13px] font-normal text-muted">· per-chapter counts, book sources, forms</span></span>
            <ChevronDown size={18} strokeWidth={1.8} aria-hidden className={`flex-none transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
          </button>
          {moreOpen && <MoreOptions blueprint={blueprint} patch={patch} />}
        </div>
      </PanelCard>

      <section aria-label="What will be created"
        className="flex flex-col gap-4 rounded-[22px] bg-navy p-6 text-white shadow-card xl:sticky xl:top-5">
        <div>
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">What will be created</p>
          <p className="mt-2.5 font-display text-[22px] font-bold leading-[1.2]" data-testid="rail-headline">{headline}</p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {([['Questions', blueprint.total_questions], ['Chapters', chaptersWithCount], ['Forms', blueprint.num_forms]] as const).map(([label, value]) => (
            <div key={label} className="min-w-0 rounded-[14px] bg-navy-2 p-3">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">{label}</p>
              <p className="mt-1.5 font-mono text-2xl font-semibold leading-none text-white">{value}</p>
            </div>
          ))}
        </div>

        <ol className="m-0 flex list-none flex-col p-0">
          {FLOW_STEPS.map((st, i) => (
            <li key={st.title} className={`relative flex gap-3 py-2.5 ${i < FLOW_STEPS.length - 1 ? 'before:absolute before:-bottom-1 before:left-[13px] before:top-[38px] before:w-0.5 before:bg-navy-3' : ''}`}>
              <span className={`relative z-[1] grid h-7 w-7 shrink-0 place-items-center rounded-full font-mono text-xs font-semibold ${
                i === FLOW_STEPS.length - 1 ? 'bg-accent text-navy' : 'bg-navy-2 text-mist-fixed'}`}>{i + 1}</span>
              <span>
                <span className="block font-semibold">{st.title}</span>
                <span className="block text-[13px] text-on-navy-muted">{st.note}</span>
              </span>
            </li>
          ))}
        </ol>

        {startError && (
          <div role="alert" className="rounded-[12px] border border-danger/40 bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink">
            {startError}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Button
            onClick={onStart}
            disabled={!canSubmit}
            icon={<Sparkles size={18} strokeWidth={1.8} aria-hidden />}
            className="w-full justify-center">
            Generate exam
          </Button>
          {onSaveTemplate && (
            <Button
              variant="on-navy"
              disabled={!canSubmit}
              className="w-full justify-center"
              onClick={() => {
                const name = window.prompt('Template name?', blueprint.name || 'My template');
                if (name && name.trim().length >= 1) onSaveTemplate(name.trim());
              }}>
              Save as template
            </Button>
          )}
        </div>
        {/* The blocker replaces the time note (design `genNote`). */}
        {blockedReason
          ? <p className="-mt-1.5 text-center text-xs text-on-navy-muted" role="status">{blockedReason}</p>
          : <p className="-mt-1.5 text-center text-xs text-on-navy-muted">Takes about {estMinutes} minute{estMinutes > 1 ? 's' : ''}. You can leave this page while it runs.</p>}
      </section>
    </div>
  );
}
